import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { throttle } from "lodash";
import { inertia } from "popmotion";
import { motion, useMotionValue } from "framer-motion";

import { css } from "@oddcommon/utils";
import styles from "./Carousel.module.scss";

export default function Carousel({ items, className, style, onPositionChange, ...props }) {
  if (items.length < 1) {
    throw new Error("Carousel requires at least one item");
  }

  // Track all elements in the carousel
  const itemRefs = useRef([]);
  // Cut out extras when array shrinks
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items.length]);

  const endPadding = useMotionValue(0);
  useEffect(() => {
    const update = () => {
      const first = itemRefs.current[0];
      const last = itemRefs.current[itemRefs.current.length - 1];
      endPadding.set(scrollElementRef.current.offsetWidth - first.offsetLeft - last.offsetWidth);
    };
    update();
    setTimeout(update, 500);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
    };
  }, [endPadding]);

  // devices where the primary pointing device is “coarse” (i.e. a finger) hide the cursor
  const [hasCursor, setHasCursor] = useState(true);
  useEffect(() => {
    setHasCursor(!window.matchMedia("(pointer: coarse)").matches);
  }, []);

  const mouseDragInertia = useRef();
  const scrollElementRef = useRef();

  // Get index, item, and scroll position of the element that lies closest to the target scroll
  // position
  const closestItemTo = (targetX) => {
    const positions = itemRefs.current.map((el, _, arr) => el.offsetLeft - arr[0].offsetLeft);
    const dest = [...positions].sort((a, b) => Math.abs(targetX - a) - Math.abs(targetX - b))[0];
    return {
      position: dest,
      index: positions.indexOf(dest),
      el: itemRefs.current[positions.indexOf(dest)],
    };
  };

  return (
    <motion.div
      {...props}
      className={css(styles.scroller, className)}
      style={{ ...style }}
      ref={scrollElementRef}
      onScroll={throttle(() => {
        if (onPositionChange) {
          onPositionChange(closestItemTo(scrollElementRef.current.scrollLeft).index);
        }
      }, 100)}
    >
      {items.map(({ el, attribution }, i) => (
        <>
          <motion.div
            key={i}
            ref={(e) => {
              itemRefs.current[i] = e;
            }}
            className={styles.carouselItem}
            style={{
              gridColumn: `${i + 1}`,
              ...(hasCursor && { cursor: "grab" }),
            }}
            whileTap={hasCursor && { cursor: "grabbing" }}
            // Implement drag gesture manually on devices where the user can't use touch to scroll
            {...(hasCursor && { "data-cursor-cta": "Drag", "data-cursor-drag-mode": true })}
            onPanStart={() => {
              // Stop any existing animation
              if (mouseDragInertia.current) mouseDragInertia.current.stop();
            }}
            onPan={(e, { delta }) => {
              e.preventDefault();
              scrollElementRef.current.scrollLeft -= delta.x;
            }}
            onPanEnd={(e, { velocity }) => {
              // Start an inertial animation to snap to a point on the carousel
              mouseDragInertia.current = inertia({
                from: scrollElementRef.current.scrollLeft,
                velocity: -velocity.x,
                modifyTarget: (target) => closestItemTo(target).position,
                onUpdate(value) {
                  scrollElementRef.current.scrollLeft = value;
                },
                onComplete() {
                  mouseDragInertia.current = null;
                },
                bounceStiffness: 200,
                bounceDamping: 40,
                timeConstant: 200,
                restDelta: 1,
                restSpeed: 10,
                power: 0.3,
              });
            }}
          >
            {el}
          </motion.div>
          {attribution?.trim?.() && (
            <div
              className={styles.attribution}
              style={{
                gridColumn: `${i + 1}`,
              }}
            >
              {attribution}
            </div>
          )}
        </>
      ))}
      {/* Safari doesn't respect the padding at the end of the scroll container */}
      <motion.div
        className={styles.spacer}
        style={{ width: endPadding, gridColumn: `${items.length + 1}` }}
      />
    </motion.div>
  );
}

Carousel.propTypes = {
  children: PropTypes.arrayOf(
    PropTypes.shape({
      el: PropTypes.node.isRequired,
      attribution: PropTypes.node,
    }),
  ).isRequired,
};
